import React, { useContext, useEffect, useState } from "react";
import {
  Box,
  Card,
  Grid,
  Typography,
  Avatar,
  CircularProgress,
  Stack,
  Divider,
  Button,
  Container,
  Paper,
  useTheme,
} from "@mui/material";
import EditIcon from "@mui/icons-material/Edit";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import AccountBalanceIcon from "@mui/icons-material/AccountBalance";
import ContactPhoneIcon from "@mui/icons-material/ContactPhone";
import BusinessIcon from "@mui/icons-material/Business";
import PsContext from "../../context";
import { _LIST_AGENCIES } from "../../models/agenciesModel";
import PsOffCanvasWindow from "../../components/PsOffCanvasWindow";
import NewEditPartner from "./newEditPartner";

const ResellerSetup = () => {
  const theme = useTheme();
  const context = useContext(PsContext);
  const [loader, setLoader] = useState(false);
  const [resellerData, setResellerData] = useState(null);
  const [editModal, setEditModal] = useState(false);

  useEffect(() => {
    if (context.userData) {
      loadReseller(context.userData.agent_id);
    }
  }, [context.userData]);

  const loadReseller = (agent_id = false) => {
    if (!context.userData.role) return;
    setLoader(true);

    _LIST_AGENCIES({ agent_id }).then((res) => {
      if (res.status === "1") {
        setResellerData(res.data[0]);
      }
      setLoader(false);
    });
  };

  const handleEdit = () => {
    setEditModal(true);
  };

  const Section = ({ icon, title, children, color }) => (
    <Paper
      elevation={3}
      sx={{
        p: 2,
        mb: 2,
        borderRadius: 3,
        background: color,
      }}
    >
      <Stack direction="row" alignItems="center" spacing={1} mb={2}>
        {icon}
        <Typography variant="h6" fontWeight={600} color="primary">
          {title}
        </Typography>
      </Stack>
      <Grid container spacing={2}>
        {children}
      </Grid>
    </Paper>
  );

  const InfoItem = ({ label, value }) => (
    <Grid item xs={12} sm={6}>
      <Typography variant="caption" color="text.secondary">
        {label}
      </Typography>
      <Typography variant="body1" fontWeight={500}>
        {value || "N/A"}
      </Typography>
    </Grid>
  );

  return (
    <>
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Card elevation={3} sx={{ borderRadius: 4, p: 3 }}>
          {loader ? (
            <Box display="flex" justifyContent="center" py={8}>
              <CircularProgress size={60} thickness={4} color="secondary" />
            </Box>
          ) : (
            resellerData && (
              <>
                <Stack
                  direction={{ xs: "column", sm: "row" }}
                  spacing={3}
                  alignItems="center"
                  mb={3}
                >
                  <Avatar
                    src={resellerData.company_logo || ""}
                    sx={{
                      width: 100,
                      height: 100,
                      bgcolor: "primary.main",
                      color: "white",
                      fontSize: 40,
                    }}
                  >
                    {resellerData.company_logo
                      ? null
                      : resellerData.company_name.charAt(0)}
                  </Avatar>
                  <Box textAlign={{ xs: "center", sm: "left" }}>
                    <Typography variant="h4" fontWeight={700}>
                      {resellerData.company_name}
                    </Typography>
                    <Typography variant="subtitle1" color="text.secondary">
                      {resellerData.contact_person}
                    </Typography>
                  </Box>
                </Stack>

                <Divider sx={{ mb: 3 }} />

                <Section
                  icon={<BusinessIcon color="primary" />}
                  title="Company Information"
                  color={theme.palette.grey[100]}
                >
                  <InfoItem label="GST ID" value={resellerData.gst_id} />
                  <InfoItem
                    label="Domain Name"
                    value={resellerData.domain_name}
                  />
                  <InfoItem label="Sender ID" value={resellerData.sender_id} />
                  <InfoItem label="Website" value={resellerData.website} />
                </Section>

                <Section
                  icon={<ContactPhoneIcon color="primary" />}
                  title="Contact Details"
                  color={theme.palette.grey[200]}
                >
                  <InfoItem label="Mobile No" value={resellerData.mobile_no} />
                  <InfoItem
                    label="WhatsApp No"
                    value={resellerData.whatsapp_no}
                  />
                  <InfoItem label="Email" value={resellerData.email} />
                  <InfoItem
                    label="Contact Person"
                    value={resellerData.contact_person}
                  />
                </Section>

                <Section
                  icon={<AccountBalanceIcon color="primary" />}
                  title="Bank Information"
                  color={theme.palette.grey[300]}
                >
                  <InfoItem
                    label="Account Name"
                    value={resellerData.account_name}
                  />
                  <InfoItem
                    label="Account No"
                    value={resellerData.account_no}
                  />
                  <InfoItem label="Bank Name" value={resellerData.bank_name} />
                  <InfoItem label="IFSC Code" value={resellerData.ifsc_code} />
                </Section>

                <Section
                  icon={<LocationOnIcon color="primary" />}
                  title="Address Details"
                  color={theme.palette.grey[400]}
                >
                  <InfoItem label="Address" value={resellerData.address1} />
                  <InfoItem label="City" value={resellerData.city} />
                  <InfoItem label="State" value={resellerData.state_name} />
                  <InfoItem label="Country" value={resellerData.country} />
                </Section>

                <Box sx={{ mt: 3, textAlign: "right" }}>
                  <Button
                    variant="contained"
                    color="secondary"
                    onClick={(e) => handleEdit()}
                    startIcon={<EditIcon />}
                  >
                    Edit Details
                  </Button>
                </Box>
              </>
            )
          )}
        </Card>
      </Container>

      <PsOffCanvasWindow
        title={"Partner Management"}
        onHide={(e) => {
          setEditModal(false);
          loadReseller(context.userData.agent_id);
        }}
        show={editModal}
        size="lg"
      >
        {editModal && <NewEditPartner dataSource={resellerData} />}
      </PsOffCanvasWindow>
    </>
  );
};

export default ResellerSetup;
